import * as React from "react";
import { Text, Badge, Tooltip, Code, HoverCard, Group, ActionIcon, CopyButton, Stack } from "@mantine/core";
import { Check, Copy } from "lucide-react";
import { ColumnFormattingRule } from "../../lib";

export interface SmartCellProps {
    value: any;
    type?: string;
    onClick?: () => void;
    isId?: boolean;
    size?: string;
    formattingConfig?: ColumnFormattingRule[];
    customStyle?: React.CSSProperties;
}

export const SmartCell: React.FC<SmartCellProps> = ({
    value,
    type,
    onClick,
    isId,
    size = "sm",
    customStyle,
}) => {
    if (value === null || value === undefined) {
        return (
            <Text c="dimmed" fs="italic" size={size}>
                NULL
            </Text>
        );
    }

    const finalStyles: React.CSSProperties = { ...customStyle };
    if (finalStyles.backgroundColor) {
        finalStyles.padding = '2px 6px';
        finalStyles.borderRadius = '4px';
        finalStyles.display = 'inline-block';
    }

    const content = (() => {
        switch (type) {
            case "numeric": {
                const numValue = typeof value === "number" ? value : Number(value);
                const isNumeric = typeof value === "number" || (!isNaN(numValue) && value !== "" && value !== null);
                let displayValue = isNumeric ? numValue.toLocaleString() : String(value);
                if (displayValue.length > 120) displayValue = displayValue.substring(0, 120) + "...";

                return (
                    <Text ta="left" ff="monospace" size={size} style={finalStyles} truncate="end" lineClamp={1}>
                        {displayValue}
                    </Text>
                );
            }
            case "date": {
                let displayValue = new Date(value).toLocaleString();
                if (displayValue.length > 120) displayValue = displayValue.substring(0, 120) + "...";
                return (
                    <Text size={size} style={finalStyles} truncate="end" lineClamp={1}>
                        {displayValue}
                    </Text>
                );
            }
            case "boolean":
                return (
                    <Badge color={value ? "green" : "red"} variant="light" size={size} style={finalStyles}>
                        {value ? "Yes" : "No"}
                    </Badge>
                );
            case "json":
                return (
                    <HoverCard width={320} shadow="md" withArrow openDelay={200}>
                        <HoverCard.Target>
                            <Code block style={{ maxHeight: "24px", overflow: "hidden", cursor: 'help', whiteSpace: 'nowrap', textOverflow: 'ellipsis', ...finalStyles }}>
                                {JSON.stringify(value)}
                            </Code>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                            <Stack gap="xs">
                                <Group justify="space-between" align="center">
                                    <Text size="xs" fw={700} c="dimmed">JSON Data</Text>
                                    <CopyButton value={JSON.stringify(value, null, 2)} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? 'Copied' : 'Copy JSON'} withArrow position="right">
                                                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy} size="sm">
                                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Group>
                                <Code block style={{ maxHeight: "300px", overflow: "auto", fontSize: '11px' }}>
                                    {JSON.stringify(value, null, 2)}
                                </Code>
                            </Stack>
                        </HoverCard.Dropdown>
                    </HoverCard>
                );
            default: {
                let displayValue = String(value);
                if (displayValue.length > 120) displayValue = displayValue.substring(0, 120) + "...";
                return (
                    <span
                        style={finalStyles}
                        title={
                            typeof value === "object" && value !== null
                                ? JSON.stringify(value, null, 2)
                                : String(value)
                        }
                    >
                        <Text size={size} truncate="end" lineClamp={1}>{displayValue}</Text>
                    </span>
                );
            }
        }
    })();

    if (isId && onClick) {
        return (
            <Text
                c="blue"
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={onClick}
                size={size}
            >
                {content}
            </Text>
        );
    }

    return content;
};
